import { ComponentRef, Injectable, OnDestroy } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { combineLatest, fromEvent, Observable, of } from 'rxjs';
import { distinctUntilChanged, map, shareReplay, startWith, switchMap } from 'rxjs/operators';
import { ApplicationService } from '../utilities/application/application.service';
import { DataService } from '../form-core/data/data.service';
import { StepModel } from '../form-json.types';
import { State } from '../store';
import { selectStepID } from '../store/router-reducers';
import { selectStepsValidity } from '../store/step-validity';
import { Navigation, NavigationComponent } from './navigation.component';
import { NavigationModule } from './navigation.module';

@Injectable({
  providedIn: NavigationModule
})
export class NavigationService implements OnDestroy {

  private navigationComponent: ComponentRef<NavigationComponent> | undefined
  private _steps: Map<string, Navigation> = new Map()

  constructor(private store: Store<State>, private app: ApplicationService, private data: DataService) { }

  ngOnDestroy(): void {
    this.destroy()
  }

  create() {
    this.navigationComponent = this.app.attach(NavigationComponent, document.body)
    this.data.getSteps().forEach((stepid) => {
      const stepModel = this.data.getModel<StepModel>(stepid)
      const navigation: Navigation = {
        name: stepModel.attributes.text,
        url: `/step/${stepModel.id}`,
        valid: false,
        after: true,
        current: false,
        presence: true
      }
      this._steps.set(stepModel.id, navigation)
    })
    this.navigationComponent.instance.navigations$ = combineLatest([
      this.store.pipe(select(selectStepID)),
      this.store.pipe(select(selectStepsValidity))
    ]).pipe(
      map(([param, stepsValidity]) => {
        let index = 0
        let currentIndex: number
        this._steps.forEach((step, key) => {
          if (key === param) currentIndex = index
          const valid = stepsValidity[key] === 'VALID'
          const after = key !== param && currentIndex !== undefined
          const current = key === param
          this._steps.set(key, { ...step, valid, after, current })
          index++
        })
        return Array.from(this._steps.values())
      })
    )
    this.navigationComponent.instance.animationState$ = this.store.pipe(
      select(selectStepID),
      switchMap<string | undefined, Observable<'displayNavigations' | 'displayLogo'>>(step => {
        const currentIndex = Array.from(this._steps.keys()).findIndex(id => id === step)
        if (currentIndex === 0) return fromEvent(window, 'scroll').pipe(
          startWith(''),
          map(() => document.documentElement.scrollTop > 40 ? 'displayNavigations' : 'displayLogo'),
          distinctUntilChanged()
        )
        return of('displayNavigations')
      }),
      shareReplay({bufferSize: 1, refCount: true})
    )
  }

  destroy() {
    if (this.navigationComponent) this.app.destroy(this.navigationComponent)
  }
}
